/**
 * Custom hook for managing file uploads with background processing
 * Handles multiple concurrent uploads with polling for processing status
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import filesService from '../services/files'
import toast from 'react-hot-toast'

const useFileUpload = (projectId) => {
  const [uploads, setUploads] = useState({})
  const pollTimeouts = useRef({})

  /**
   * Clean up polling timeouts on unmount
   */
  useEffect(() => {
    return () => {
      Object.values(pollTimeouts.current).forEach((timeout) => clearTimeout(timeout))
    }
  }, [])

  /**
   * Poll file status until processing completes
   */
  const pollFileStatus = useCallback(
    (fileId) => {
      const poll = async () => {
        try {
          const file = await filesService.getFile(fileId)

          // Get the filename before updating state
          const fileName = file.original_filename || 'Unknown'

          setUploads((prev) => ({
            ...prev,
            [fileId]: {
              ...prev[fileId],
              status: file.status,
              progress: file.status === 'processing' ? 50 : file.status === 'completed' ? 100 : 0,
              error: file.error_message,
            },
          }))

          if (file.status === 'completed') {
            toast.success(`File uploaded successfully: ${fileName}`)
            // Clean up this poll
            if (pollTimeouts.current[fileId]) {
              clearTimeout(pollTimeouts.current[fileId])
              delete pollTimeouts.current[fileId]
            }
          } else if (file.status === 'failed') {
            toast.error(`File upload failed: ${file.error_message || 'Unknown error'}`)
            // Clean up this poll
            if (pollTimeouts.current[fileId]) {
              clearTimeout(pollTimeouts.current[fileId])
              delete pollTimeouts.current[fileId]
            }
          } else {
            // Continue polling
            pollTimeouts.current[fileId] = setTimeout(poll, 2000)
          }
        } catch (error) {
          console.error('Error polling file status:', error)
          setUploads((prev) => ({
            ...prev,
            [fileId]: {
              ...prev[fileId],
              status: 'failed',
              error: error.response?.data?.detail || 'Failed to check file status',
            },
          }))
          toast.error('Failed to check file upload status')

          // Clean up this poll
          if (pollTimeouts.current[fileId]) {
            clearTimeout(pollTimeouts.current[fileId])
            delete pollTimeouts.current[fileId]
          }
        }
      }

      poll()
    },
    [setUploads]
  )

  /**
   * Upload a single file
   */
  const uploadFile = useCallback(
    async (file, category = 'uploaded') => {
      // Validate file size (100MB max)
      const MAX_FILE_SIZE = 100 * 1024 * 1024 // 100MB
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`File "${file.name}" exceeds 100MB limit`)
        return null
      }

      // Generate temporary ID for tracking
      const tempId = `temp-${Date.now()}-${Math.random()}`

      try {
        // Add to uploads with pending status
        setUploads((prev) => ({
          ...prev,
          [tempId]: {
            id: tempId,
            name: file.name,
            size: file.size,
            status: 'uploading',
            progress: 0,
            error: null,
          },
        }))

        // Upload the file
        const uploadedFile = await filesService.uploadFile(projectId, file, category)

        // Update with real ID and start polling
        setUploads((prev) => {
          const newUploads = { ...prev }
          delete newUploads[tempId]
          newUploads[uploadedFile.id] = {
            id: uploadedFile.id,
            name: file.name,
            size: file.size,
            status: uploadedFile.status,
            progress: 25,
            error: null,
          }
          return newUploads
        })

        // Start polling for processing status
        pollFileStatus(uploadedFile.id)

        return uploadedFile.id
      } catch (error) {
        console.error('Error uploading file:', error)
        setUploads((prev) => ({
          ...prev,
          [tempId]: {
            ...prev[tempId],
            status: 'failed',
            error: error.response?.data?.detail || 'Upload failed',
          },
        }))
        toast.error(`Failed to upload "${file.name}"`)
        return null
      }
    },
    [projectId, pollFileStatus]
  )

  /**
   * Upload multiple files
   */
  const uploadFiles = useCallback(
    async (files, category = 'uploaded') => {
      const fileArray = Array.from(files)
      const uploadPromises = fileArray.map((file) => uploadFile(file, category))
      const fileIds = await Promise.all(uploadPromises)
      return fileIds.filter((id) => id !== null)
    },
    [uploadFile]
  )

  /**
   * Remove upload from tracking
   */
  const removeUpload = useCallback((fileId) => {
    setUploads((prev) => {
      const newUploads = { ...prev }
      delete newUploads[fileId]
      return newUploads
    })

    // Clean up polling timeout if exists
    if (pollTimeouts.current[fileId]) {
      clearTimeout(pollTimeouts.current[fileId])
      delete pollTimeouts.current[fileId]
    }
  }, [])

  /**
   * Clear all completed uploads
   */
  const clearCompleted = useCallback(() => {
    setUploads((prev) => {
      const newUploads = {}
      Object.entries(prev).forEach(([id, upload]) => {
        if (upload.status !== 'completed' && upload.status !== 'failed') {
          newUploads[id] = upload
        }
      })
      return newUploads
    })
  }, [])

  /**
   * Clear all uploads
   */
  const clearAll = useCallback(() => {
    // Clean up all polling timeouts
    Object.values(pollTimeouts.current).forEach((timeout) => clearTimeout(timeout))
    pollTimeouts.current = {}

    setUploads({})
  }, [])

  return {
    uploads: Object.values(uploads),
    uploadFile,
    uploadFiles,
    removeUpload,
    clearCompleted,
    clearAll,
  }
}

export default useFileUpload
